import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Dashboard from './Dashboard';
import './QrCodeDisplay.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://vakra.co.in/api';
const FALLBACK_SHOP_IMAGE = 'https://via.placeholder.com/120x120?text=Shop';

const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized));
  } catch {
    return {};
  }
};

const normalizeVendorType = (value) => {
  const type = String(value || '').trim().toLowerCase();
  if (type === 'food' || type === 'fast_food') return 'fastfood';
  if (type === 'garment') return 'garments';
  return type || 'fastfood';
};

const normalizeGroceryMode = (value) => {
  const mode = String(value || '').trim().toLowerCase();
  return mode === 'retail' || mode === 'wholesale' ? mode : '';
};

const normalizeQrItem = (item = {}) => ({
  ...item,
  id: item.id || item.qrId || item.qr_id,
  qrName: item.qrName || item.qr_name || item.name || '',
  tableNumber: item.tableNumber || item.table_number || item.table || '',
  qrCode: item.qrCode || item.qr_code || item.qrImage || item.qr_image || item.image || '',
  qrLink: item.qrLink || item.qr_link || item.url || item.link || item.qrUrl || item.qr_url || '',
  qrToken: item.qrToken || item.qr_token || item.token || '',
  groceryMode: item.groceryMode || item.grocery_mode || '',
  createdAt: item.createdAt || item.created_at || '',
  isActive: item.isActive ?? item.is_active ?? item.active ?? item.qr_active ?? true,
});

function QrCodeDisplay() {
  const [token] = useState(localStorage.getItem('token') || '');
  const [vendorId, setVendorId] = useState('');
  const [vendorType, setVendorType] = useState('fastfood');
  const [groceryMode, setGroceryMode] = useState('');
  const [shopName, setShopName] = useState('');
  const [shopImage, setShopImage] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [qrName, setQrName] = useState('');
  const [newQrActive, setNewQrActive] = useState(true);
  const [qrList, setQrList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false);
  const [previewQr, setPreviewQr] = useState(null);
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, type, message });
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => {
      setToast({ show: false, type: 'success', message: '' });
    }, 2800);
  };

  const businessLabel = useMemo(() => {
    if (vendorType === 'fastfood') return 'Fast Food';
    if (vendorType === 'grocery') return groceryMode === 'wholesale' ? 'Grocery Wholesale' : 'Grocery Retail';
    if (vendorType === 'cosmetic') return 'Cosmetic';
    if (vendorType === 'garments') return 'Garments';
    return 'Business';
  }, [vendorType, groceryMode]);

  const backPath = useMemo(() => {
    if (vendorType === 'fastfood') return '/Food';
    if (vendorType === 'cosmetic') return '/cosmetic';
    if (vendorType === 'garments') return '/garments';
    if (vendorType === 'grocery') return groceryMode === 'wholesale' ? '/grocery-wholesale' : '/grocery-retail';
    return '/dashboard';
  }, [vendorType, groceryMode]);

  const summary = useMemo(() => {
    const tables = new Set(qrList.map((item) => String(item.tableNumber || '').trim()).filter(Boolean));
    const latest = [...qrList]
      .filter((item) => item.createdAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    let lastUpdated = 'Not Available';
    if (latest?.createdAt) {
      const date = new Date(latest.createdAt);
      if (!Number.isNaN(date.getTime())) {
        lastUpdated = date.toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }

    const activeQrCodes = qrList.filter((item) => Boolean(item.isActive)).length;
    const inactiveQrCodes = Math.max(0, qrList.length - activeQrCodes);

    return { totalQrCodes: qrList.length, activeQrCodes, inactiveQrCodes, totalTables: tables.size, lastUpdated };
  }, [qrList]);

  const buildFallbackQrLink = (item = {}) => {
    const params = new URLSearchParams();
    if (vendorId) params.set('vendorId', vendorId);
    params.set('businessType', vendorType || 'fastfood');
    if (item.qrName) params.set('qrName', item.qrName);
    if (item.tableNumber) params.set('table', item.tableNumber);
    if (vendorType === 'grocery' && groceryMode) params.set('groceryMode', groceryMode);
    return `${window.location.origin}/qr-form?${params.toString()}`;
  };

  const getQrPublicLink = (item = {}) => {
    // New secure QR: backend agar qr_token/qrToken bhejta hai to URL me vendorId/table/qrName show nahi hoga.
    // Old QR: agar token nahi hai to purana qrLink/old query link same rahega.
    if (item.qrToken) {
      return `${window.location.origin}/qr-form/${encodeURIComponent(item.qrToken)}`;
    }

    if (item.qrLink) {
      return item.qrLink;
    }

    return buildFallbackQrLink(item);
  };

  const getQrIsActive = (item) => Boolean(item?.isActive);

  const allQrChecked = qrList.length > 0 && qrList.every(getQrIsActive);
  const someQrChecked = qrList.some(getQrIsActive);

  const fetchProfileAndQrList = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const decoded = decodeToken(token);
      const finalVendorId = decoded.userId || decoded.vendorId || decoded.vendor_id || decoded.id || '';
      const finalVendorType = normalizeVendorType(
        decoded.vendorType || decoded.vendor_type || decoded.businessType || decoded.business_type || 'fastfood'
      );
      const finalGroceryMode = normalizeGroceryMode(decoded.groceryMode || decoded.grocery_mode || '');

      setVendorId(String(finalVendorId || ''));
      setVendorType(finalVendorType);
      setGroceryMode(finalGroceryMode);

      const [profileResult, qrResult] = await Promise.allSettled([
        axios.get(`${API_URL}/user/${finalVendorType}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/qrpage/list`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (profileResult.status === 'fulfilled') {
        const profile = profileResult.value?.data || {};
        setShopName(profile.shop_name || profile.shopName || profile.name || 'Unnamed Shop');
        setShopImage(profile.shop_image || profile.shopImage || profile.image || profile.logo || '');
        if (finalVendorType === 'grocery') {
          const modeFromProfile = normalizeGroceryMode(profile.groceryMode || profile.grocery_mode);
          if (modeFromProfile) setGroceryMode(modeFromProfile);
        }
      }

      if (qrResult.status === 'fulfilled') {
        const raw = qrResult.value?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.qrCodes)
            ? raw.qrCodes
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        setQrList(list.map(normalizeQrItem));
      } else {
        setQrList([]);
      }
    } catch (error) {
      showToast(error.response?.data?.message || error.response?.data?.error || 'QR data load nahi hua.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileAndQrList();
  }, [token]);

  const handleCreateQr = async () => {
    const cleanTable = String(tableNumber || '').trim();
    const cleanName = String(qrName || '').trim() || (cleanTable ? `T${cleanTable}` : '');

    if (!cleanTable && !cleanName) {
      showToast('Table number ya QR name me se kam se kam ek detail enter karein.', 'error');
      return;
    }

    if (vendorType === 'grocery' && !groceryMode) {
      showToast('Grocery mode missing hai. Pehle retail ya wholesale mode save karein.', 'error');
      return;
    }

    try {
      setCreating(true);
      const payload = { tableNumber: cleanTable, qrName: cleanName, isActive: Boolean(newQrActive) };
      if (vendorType === 'grocery') payload.groceryMode = groceryMode;

      const response = await axios.post(`${API_URL}/qrpage/generate-qr`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showToast(response.data?.message || 'QR code successfully generate ho gaya.');
      setTableNumber('');
      setQrName('');
      setNewQrActive(true);
      await fetchProfileAndQrList();
    } catch (error) {
      showToast(error.response?.data?.message || error.response?.data?.error || 'QR generate nahi hua.', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleQrStatus = async (item, checkedValue = null) => {
    if (!item?.id || statusUpdatingId || bulkStatusUpdating) return;

    const nextActive = checkedValue === null ? !getQrIsActive(item) : Boolean(checkedValue);

    try {
      setStatusUpdatingId(item.id);

      const response = await axios.patch(
        `${API_URL}/qrpage/${item.id}/status`,
        { isActive: nextActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setQrList((current) =>
        current.map((qr) =>
          String(qr.id) === String(item.id)
            ? { ...qr, isActive: response.data?.isActive ?? nextActive }
            : qr
        )
      );

      showToast(nextActive ? 'Checkout on ho gaya.' : 'Checkout off ho gaya.');
    } catch (error) {
      showToast(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'QR status update nahi hua.',
        'error'
      );
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleToggleAllQrStatus = async (checkedValue) => {
    if (!qrList.length || bulkStatusUpdating) return;

    const nextActive = Boolean(checkedValue);
    const previousList = qrList;

    try {
      setBulkStatusUpdating(true);

      setQrList((current) =>
        current.map((qr) => ({
          ...qr,
          isActive: nextActive,
        }))
      );

      await Promise.all(
        previousList.map((qr) =>
          axios.patch(
            `${API_URL}/qrpage/${qr.id}/status`,
            { isActive: nextActive },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      showToast(nextActive ? 'Sabhi checkout on ho gaye.' : 'Sabhi checkout off ho gaye.');
    } catch (error) {
      setQrList(previousList);
      showToast(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Sabhi QR status update nahi hue.',
        'error'
      );
    } finally {
      setBulkStatusUpdating(false);
    }
  };

  const handleDeleteQr = async (id) => {
    if (!id || !window.confirm('Kya aap is QR code ko delete karna chahte hain?')) return;
    try {
      setDeletingId(id);
      await axios.delete(`${API_URL}/qrpage/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQrList((current) => current.filter((item) => String(item.id) !== String(id)));
      showToast('QR code delete ho gaya.');
    } catch (error) {
      showToast(error.response?.data?.message || error.response?.data?.error || 'QR delete nahi hua.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyLink = async (item) => {
    const link = getQrPublicLink(item);
    try {
      await navigator.clipboard.writeText(link);
      showToast('QR link copy ho gaya.');
    } catch {
      const input = document.createElement('input');
      input.value = link;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
      showToast('QR link copy ho gaya.');
    }
  };

  const handleDownloadQr = async (item) => {
    if (!item.qrCode) {
      showToast('QR image available nahi hai.', 'error');
      return;
    }

    const safeName = String(item.qrName || item.tableNumber || 'qr-code')
      .replace(/[^a-zA-Z0-9-_]/g, '-')
      .replace(/-+/g, '-');
    const fileName = `${safeName || 'qr-code'}.png`;

    try {
      if (String(item.qrCode).startsWith('data:image')) {
        const link = document.createElement('a');
        link.href = item.qrCode;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await fetch(item.qrCode);
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
      }
      showToast('QR code download ho gaya.');
    } catch {
      window.open(item.qrCode, '_blank', 'noopener,noreferrer');
      showToast('QR image open ho gayi. Download option select karein.');
    }
  };

  const handlePrintQr = (item) => {
    if (!item.qrCode) return showToast('Print ke liye QR image available nahi hai.', 'error');
    const printWindow = window.open('', '_blank', 'width=600,height=760');
    if (!printWindow) return showToast('Popup blocked hai. Browser me popup allow karein.', 'error');

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body{font-family:Arial,sans-serif;text-align:center;padding:28px;color:#111827}
            .card{max-width:420px;margin:auto;border:2px solid #e50914;border-radius:18px;padding:22px}
            .brand{color:#e50914;font-weight:800;letter-spacing:1px;text-transform:uppercase;font-size:13px}
            h1{margin:8px 0 4px;font-size:26px} h2{margin:8px 0;color:#e50914}
            p{margin:6px 0;color:#4b5563} img{width:290px;height:290px;object-fit:contain;margin:16px auto;display:block}
            .scan{font-weight:800;font-size:16px}
          </style>
        </head>
        <body>
          <div class="card">
            <div class="brand">Scan To Order</div>
            <h1>${shopName || 'Restaurant'}</h1>
            <h2>${item.qrName || 'QR Code'}</h2>
            <p>Table Number: ${item.tableNumber || '-'}</p>
            <img src="${item.qrCode}" alt="QR Code" />
            <div class="scan">Scan QR Code To Place Your Order</div>
          </div>
          <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
        </body>
      </html>
    `);
    printWindow.document.close();
    showToast('Print window open ho rahi hai.');
  };

  if (!token) {
    return (
      <div className="qrm-login-page">
        <div className="qrm-login-card">
          <div className="qrm-login-icon">🔐</div>
          <h2>Please Login</h2>
          <p>QR codes manage karne ke liye vendor login required hai.</p>
          <Link to="/login" className="qrm-primary-btn">Go To Login</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dashboard />

      <main className="qrm-page page-below-navbar">
        {toast.show && (
          <div className={`qrm-toast ${toast.type}`}>
            <span>{toast.type === 'error' ? '⚠️' : '✅'}</span>
            <p>{toast.message}</p>
            <button type="button" onClick={() => setToast({ show: false, type: 'success', message: '' })}>×</button>
          </div>
        )}

        <section className="qrm-topbar">
          <div className="qrm-title-wrap">
            <Link to={backPath} className="qrm-back">←</Link>
            <div>
              <span className="qrm-eyebrow">QR Ordering System</span>
              <h1>QR Code Management</h1>
              <p>Create and manage QR codes for your restaurant tables.</p>
            </div>
          </div>
          <div className="qrm-profile-mini">
            <img src={shopImage || FALLBACK_SHOP_IMAGE} alt={shopName || 'Shop'} />
            <div>
              <strong>{shopName || 'Restaurant Owner'}</strong>
              <span>{businessLabel}</span>
            </div>
          </div>
        </section>

        {loading ? (
          <section className="qrm-loading">
            <div className="qrm-loader" />
            <strong>QR codes load ho rahe hain...</strong>
          </section>
        ) : (
          <>
            <section className="qrm-summary-grid">
              <article className="qrm-summary-card red"><span>▦</span><div><small>Total QR Codes</small><strong>{summary.totalQrCodes}</strong><p>{summary.activeQrCodes} checkout on / {summary.inactiveQrCodes} off</p></div></article>
              <article className="qrm-summary-card orange"><span>♨</span><div><small>Business Type</small><strong>{businessLabel}</strong><p>Restaurant</p></div></article>
              <article className="qrm-summary-card green"><span>▤</span><div><small>Total Tables</small><strong>{summary.totalTables}</strong><p>Tables created</p></div></article>
              <article className="qrm-summary-card purple"><span>◷</span><div><small>Last Updated</small><strong className="small">{summary.lastUpdated}</strong><p>QR records</p></div></article>
            </section>

            <section className="qrm-card qrm-create-card">
              <div className="qrm-section-head">
                <div>
                  <h2>Create New QR Code</h2>
                  <p>Table aur QR name enter karke new QR code generate karein.</p>
                </div>
              </div>

              <div className="qrm-create-row">
                <label>
                  <span>Table Number <small>Optional</small></span>
                  <input value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="e.g., 1, 2, 3..." />
                </label>
                <label>
                  <span>QR Code Name <small>Optional</small></span>
                  <input value={qrName} onChange={(e) => setQrName(e.target.value)} placeholder="e.g., T1, VIP, Family Area..." />
                </label>
                <label className="qrm-create-active-control">
                  <input
                    type="checkbox"
                    checked={newQrActive}
                    onChange={(e) => setNewQrActive(e.target.checked)}
                  />
                  <span>Checkout</span>
                </label>
                <button type="button" className="qrm-primary-btn" disabled={creating} onClick={handleCreateQr}>
                  {creating ? 'Generating...' : '+ Generate QR Code'}
                </button>
              </div>

              <div className="qrm-note">ⓘ Table number aur QR name enter karne se identify aur manage karna easy rahega.</div>
            </section>

            <section className="qrm-card qrm-list-card">
              <div className="qrm-section-head">
                <div>
                  <h2>Your QR Codes</h2>
                  <p>Preview, download, print, copy link aur delete actions ek hi jagah.</p>
                </div>
                <div className="qrm-bulk-status">
                  <label className="qrm-check-pill" aria-label="Checkout All">
                    <input
                      type="checkbox"
                      checked={allQrChecked}
                      ref={(node) => {
                        if (node) node.indeterminate = !allQrChecked && someQrChecked;
                      }}
                      disabled={!qrList.length || bulkStatusUpdating}
                      onChange={(e) => handleToggleAllQrStatus(e.target.checked)}
                    />
                    <span>Checkout All</span>
                  </label>
                  <strong>{qrList.length} QR Codes</strong>
                </div>
              </div>

              {qrList.length === 0 ? (
                <div className="qrm-empty">
                  <div>▦</div>
                  <h3>No QR Codes Available</h3>
                  <p>Upar form se pehla QR code generate karein.</p>
                </div>
              ) : (
                <>
                  <div className="qrm-desktop-table">
                    <div className="qrm-table-head"><span>#</span><span>Table Number</span><span>QR Name</span><span>Checkout</span><span>QR Code</span><span>Actions</span></div>
                    {qrList.map((item, index) => (
                      <div className="qrm-table-row" key={item.id || `${item.qrName}-${index}`}>
                        <span>{index + 1}</span>
                        <strong>{item.tableNumber || '-'}</strong>
                        <span className="qrm-pill">{item.qrName || 'QR Code'}</span>
                        <label className="qrm-status-switch" aria-label="Checkout">
                          <input
                            type="checkbox"
                            checked={getQrIsActive(item)}
                            disabled={String(statusUpdatingId) === String(item.id) || bulkStatusUpdating}
                            onChange={(e) => handleToggleQrStatus(item, e.target.checked)}
                          />
                        </label>
                        <img className="qrm-qr-img" src={item.qrCode} alt={item.qrName || 'QR Code'} />
                        <div className="qrm-actions">
                          <button className="view" type="button" onClick={() => setPreviewQr(item)}>👁</button>
                          <button className="download" type="button" onClick={() => handleDownloadQr(item)}>↓</button>
                          <button className="print" type="button" onClick={() => handlePrintQr(item)}>🖨</button>
                          <button className="copy" type="button" onClick={() => handleCopyLink(item)}>🔗</button>
                          <button className="delete" type="button" disabled={String(deletingId) === String(item.id)} onClick={() => handleDeleteQr(item.id)}>{String(deletingId) === String(item.id) ? '...' : '🗑'}</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="qrm-mobile-list">
                    {qrList.map((item, index) => (
                      <article className="qrm-mobile-card" key={item.id || `${item.qrName}-${index}`}>
                        <div className="qrm-mobile-number">{item.tableNumber || index + 1}</div>
                        <div className="qrm-mobile-main">
                          <span className="qrm-pill">{item.qrName || 'QR Code'}</span>
                          <small>Table {item.tableNumber || '-'}</small>
                        </div>
                        <label className="qrm-status-switch qrm-mobile-status-switch" aria-label="Checkout">
                          <input
                            type="checkbox"
                            checked={getQrIsActive(item)}
                            disabled={String(statusUpdatingId) === String(item.id) || bulkStatusUpdating}
                            onChange={(e) => handleToggleQrStatus(item, e.target.checked)}
                          />
                        </label>
                        <img className="qrm-mobile-qr" src={item.qrCode} alt={item.qrName || 'QR Code'} />
                        <div className="qrm-mobile-actions">
                          <button className="download" type="button" onClick={() => handleDownloadQr(item)}>↓</button>
                          <button className="print" type="button" onClick={() => handlePrintQr(item)}>🖨</button>
                          <button className="copy" type="button" onClick={() => handleCopyLink(item)}>🔗</button>
                          <button className="delete" type="button" disabled={String(deletingId) === String(item.id)} onClick={() => handleDeleteQr(item.id)}>{String(deletingId) === String(item.id) ? '...' : '🗑'}</button>
                        </div>
                      </article>
                    ))}
                  </div>
                </>
              )}
            </section>

            <Link to={backPath} className="qrm-back-link">← Back</Link>
          </>
        )}

        {previewQr && (
          <div className="qrm-modal-bg" onClick={() => setPreviewQr(null)}>
            <section className="qrm-modal" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="qrm-modal-close" onClick={() => setPreviewQr(null)}>×</button>
              <span>QR Preview</span>
              <h2>{previewQr.qrName || 'QR Code'}</h2>
              <p>Table Number: <strong>{previewQr.tableNumber || '-'}</strong></p>
              <img src={previewQr.qrCode} alt={previewQr.qrName || 'QR Code'} />
              <div className="qrm-modal-actions">
                <button type="button" className="download" onClick={() => handleDownloadQr(previewQr)}>↓ Download</button>
                <button type="button" className="print" onClick={() => handlePrintQr(previewQr)}>🖨 Print</button>
                <button type="button" className="copy" onClick={() => handleCopyLink(previewQr)}>🔗 Copy Link</button>
              </div>
            </section>
          </div>
        )}

        <nav className="qrm-mobile-nav">
          <Link to="/Food"><span>⌂</span><small>Dashboard</small></Link>
          <Link to="/ordersReceived"><span>▤</span><small>Orders</small></Link>
          <Link to="/qr-code-display" className="active"><span>▦</span><small>QR Codes</small></Link>
          <Link to="/vendors"><span>▥</span><small>Records</small></Link>
          <Link to="/with_customer"><span>⋯</span><small>More</small></Link>
        </nav>
      </main>
    </>
  );
}

export default QrCodeDisplay;
